import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, interval } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PubNubService } from './pubnub.service';

const HERE_NOW_POLL_MS = 30_000;
const TYPING_SEND_GAP_MS = 3_000;
const TYPING_EXPIRY_MS = 5_000;

export interface CommunityPresenceState {
  /** userIds (fan_<id>) currently in the space; null until PubNub has answered (or if it cannot) */
  online: Set<string> | null;
  /** display names of other people typing */
  typing: string[];
}

/**
 * Online status and typing for one community space, behind
 * environment.enableCommunityPresence (off by default).
 * - Online: polls PubNub hereNow for the space channel. It does not subscribe to
 *   -pnpres channels, so the existing Access Manager tokens and the shared
 *   subscription are untouched.
 * - Typing: PubNub signals on the space channel the user already subscribes to.
 * On any PubNub error online stays null, so the page shows no online/offline split rather than a wrong one.
 */
@Injectable({ providedIn: 'root' })
export class CommunityPresenceService {
  private readonly pubnubService = inject(PubNubService);
  private readonly zone = inject(NgZone);
  private readonly state$ = new BehaviorSubject<CommunityPresenceState>({ online: null, typing: [] });
  private channel = '';
  private selfId = '';
  private poll?: Subscription;
  private sweep?: Subscription;
  private listener: any;
  private typingUntil = new Map<string, { name: string; until: number }>();
  private lastTypingSent = 0;

  get enabled(): boolean {
    return (environment as any).enableCommunityPresence === true;
  }

  watch(channel: string, selfId: string): Observable<CommunityPresenceState> {
    this.stop();
    if (!this.enabled || !channel || !this.pubnub) return this.state$.asObservable();
    this.channel = channel;
    this.selfId = selfId;
    this.poll = interval(HERE_NOW_POLL_MS).subscribe(() => this.refreshOnline());
    this.refreshOnline();
    this.listener = { signal: (e: any) => this.onSignal(e) };
    this.pubnub.addListener(this.listener);
    this.sweep = interval(1_000).subscribe(() => this.expireTyping());
    return this.state$.asObservable();
  }

  /** Call on every keystroke; sends at most one signal every 3 seconds. */
  notifyTyping(name: string): void {
    const now = Date.now();
    if (!this.enabled || !this.channel || now - this.lastTypingSent < TYPING_SEND_GAP_MS) return;
    this.lastTypingSent = now;
    try {
      this.pubnub.signal({ channel: this.channel, message: { t: 'typing', n: String(name || '').slice(0, 40) } })
        .catch(() => undefined);
    } catch {
      // no write permission or no client: typing is optional
    }
  }

  stop(): void {
    this.poll?.unsubscribe();
    this.sweep?.unsubscribe();
    if (this.listener && this.pubnub) this.pubnub.removeListener(this.listener);
    this.listener = null;
    this.typingUntil.clear();
    this.channel = '';
    this.state$.next({ online: null, typing: [] });
  }

  private get pubnub(): any {
    return this.pubnubService.pubnub;
  }

  private refreshOnline(): void {
    const channel = this.channel;
    try {
      this.pubnub.hereNow({ channels: [channel], includeUUIDs: true, includeState: false })
        .then((res: any) => {
          if (channel !== this.channel) return;
          const occupants = res?.channels?.[channel]?.occupants || [];
          // only signed-in fans count; placeholder ids such as 'sample-uuid' are ignored
          const online = new Set<string>(occupants.map((o: any) => String(o.uuid)).filter((id: string) => id.startsWith('fan_')));
          this.emit({ online });
        })
        .catch(() => undefined);
    } catch {
      // presence not enabled for this keyset: show nothing
    }
  }

  private onSignal(e: any): void {
    if (e?.channel !== this.channel || e?.message?.t !== 'typing' || e?.publisher === this.selfId) return;
    this.typingUntil.set(e.publisher, { name: e.message.n || 'Someone', until: Date.now() + TYPING_EXPIRY_MS });
    this.emitTyping();
  }

  private expireTyping(): void {
    const now = Date.now();
    let changed = false;
    this.typingUntil.forEach((v, k) => {
      if (v.until < now) {
        this.typingUntil.delete(k);
        changed = true;
      }
    });
    if (changed) this.emitTyping();
  }

  private emitTyping(): void {
    this.emit({ typing: Array.from(this.typingUntil.values()).map(v => v.name) });
  }

  private emit(patch: Partial<CommunityPresenceState>): void {
    this.zone.run(() => this.state$.next({ ...this.state$.value, ...patch }));
  }
}
